import { useQuery } from "@tanstack/react-query";

import {
  AlertBanner,
  ErrorState,
  OfflineBanner,
  Skeleton,
  StatusBadge,
} from "../../components/ui/Feedback";
import { formatDateTime } from "../../lib/format";
import { operationsApi, type OperationsProjection } from "../../lib/api";
import { observationStateLabel } from "../../lib/operation-events";
import { IncidentPanel } from "./IncidentPanel";
import { RetentionPanel } from "./RetentionPanel";
import { RuntimeLogViewer } from "./RuntimeLogViewer";

export function OperationsCockpit({ projectId }: { readonly projectId: string }) {
  const query = useQuery({
    queryKey: ["operations", projectId],
    queryFn: ({ signal }) => operationsApi.snapshot(projectId, signal),
    refetchInterval: 30_000,
    retry: false,
  });
  const projections = query.data?.items ?? [];
  const newest = projections
    .map((item) => item.observedAt)
    .sort()
    .at(-1);
  const disconnected = projections.some((item) => item.freshness === "disconnected");
  return (
    <section aria-labelledby="operations-cockpit-heading" className="stack">
      <div>
        <p className="eyebrow">Operasyon kokpiti</p>
        <h2 id="operations-cockpit-heading">Doğrulanmış çalışma durumu</h2>
        <p className="muted">
          Bağlantı durumu sağlık durumu değildir. Her kart son gözlem zamanını ve veri tazeliğini
          açıkça gösterir.
        </p>
      </div>
      {query.isLoading ? <Skeleton label="Operasyon durumu yükleniyor" /> : null}
      {query.isError && query.data === undefined ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : null}
      {query.isError && query.data !== undefined ? (
        <AlertBanner heading="Operasyon durumu yenilenemedi" tone="warning">
          <p>
            Son doğrulanan gözlemler gösteriliyor. Son gözlem:{" "}
            {newest === undefined ? "bilinmiyor" : formatDateTime(newest)}.
          </p>
        </AlertBanner>
      ) : null}
      {typeof navigator !== "undefined" && !navigator.onLine && query.data !== undefined ? (
        <OfflineBanner updatedAt={newest} />
      ) : null}
      {disconnected ? (
        <AlertBanner heading="Agent bağlantısı kesildi" tone="warning">
          <p>
            Kesik bağlantı sağlıklı çalışma anlamına gelmez. Son doğrulanan kanıtlar aşağıda
            zamanlarıyla görünür.
          </p>
        </AlertBanner>
      ) : null}
      {query.data === undefined ? null : (
        <>
          {projections.length === 0 ? (
            <section className="card">
              <p>Bu proje için doğrulanmış çalışma gözlemi henüz yok.</p>
            </section>
          ) : (
            <div className="dashboard-grid">
              {projections.map((projection) => (
                <ProjectionCard key={projection.workloadId} projection={projection} />
              ))}
            </div>
          )}
          <IncidentPanel projectId={projectId} projections={projections} />
          <RuntimeLogViewer projectId={projectId} />
          <RetentionPanel projectId={projectId} />
        </>
      )}
    </section>
  );
}

function ProjectionCard({ projection }: { readonly projection: OperationsProjection }) {
  const health =
    projection.health === "healthy"
      ? "healthy"
      : projection.health === "degraded"
        ? "warning"
        : projection.health === "unhealthy"
          ? "error"
          : "unknown";
  return (
    <article className="card stack">
      <div className="split-row">
        <div>
          <p className="eyebrow">Sunucu ve iş yükü</p>
          <h3 className="technical">{projection.workloadId}</h3>
        </div>
        <StatusBadge
          label={
            health === "healthy"
              ? "Sağlıklı"
              : health === "warning"
                ? "Bozulmuş"
                : health === "error"
                  ? "Sağlıksız"
                  : "Bilinmiyor"
          }
          status={health}
        />
      </div>
      <p>
        <strong>{observationStateLabel(projection.freshness)}</strong>
        <br />
        <time dateTime={projection.observedAt}>
          Son gözlem: {formatDateTime(projection.observedAt)}
        </time>
      </p>
      <dl className="definition-grid">
        {projection.metrics.map((metric) => (
          <div key={metric.name}>
            <dt>{metricLabel(metric.name)}</dt>
            <dd>{formatMetric(metric.name, metric.value)}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}
function metricLabel(name: OperationsProjection["metrics"][number]["name"]): string {
  return {
    cpu_percent: "CPU",
    memory_bytes: "Bellek",
    disk_bytes: "Disk",
    network_rx_bytes: "Ağ alım",
    network_tx_bytes: "Ağ gönderim",
  }[name];
}
function formatMetric(
  name: OperationsProjection["metrics"][number]["name"],
  value: number,
): string {
  return name === "cpu_percent"
    ? `%${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(value)}`
    : new Intl.NumberFormat("tr-TR", { style: "unit", unit: "byte", unitDisplay: "narrow" }).format(
        value,
      );
}
